import * as Sentry from '@sentry/react'
import type { AxiosRequestConfig, AxiosResponse, Method } from 'axios'
import axios from 'axios'

import type { Device, Transfer, TransferConcerns } from '@/api/definition'
import { Status } from '@/api/definition'
import { samApiClient } from '@/api/index'

export async function fetchEndpoints(): Promise<AxiosResponse> {
  return samApiClient.get('endpoint')
}

export async function takeIn(
  uid: Device['uid'],
  endpoint: string,
): Promise<Transfer> {
  return transfer('put', uid, endpoint)
}

export async function pushOut(
  uid: Device['uid'],
  endpoint: string,
): Promise<Transfer> {
  return transfer('delete', uid, endpoint)
}
async function transfer(
  method: Method,
  uid: Device['uid'],
  endpoint: string,
): Promise<Transfer> {
  try {
    const url = `device/${uid}/service`
    const requestConfig: AxiosRequestConfig = {
      url: url,
      method: method,
      data: {
        endpoint,
      },
    }
    const response = await samApiClient.request(requestConfig)
    return { status: Status.SUCCESS, data: response.data }
  } catch (error) {
    if (!axios.isAxiosError(error) || !error.response) {
      return {
        status: Status.FAILURE,
        reason: 'app:device.transfer.noResponse',
      }
    }

    let reason = 'app:device.transfer.error'
    let concerns: undefined | TransferConcerns
    const { response } = error
    const { status, data } = response

    // Extract the actual HTTP error details
    const httpErrorMessage = `Request failed with status code ${status}${
      typeof data === 'string' ? `: ${data}` : ''
    }`

    switch (status) {
      case 502:
        reason = 'app:device.transfer.failed'
        concerns = data as TransferConcerns | undefined
        break
      case 503:
        reason = 'app:device.transfer.unable'
        // Add HTTP error details to concerns
        concerns = [{ message: httpErrorMessage }]
        break
      case 504:
        reason = 'app:device.transfer.timeout'
        concerns = [{ message: httpErrorMessage }]
        break
      default:
        // Every other state than the defined cases is seen as an error. A
        // service engineer will not be able to resolve most of the errors
        // that can occur. As an example, a not found probably indicates there
        // is something wrong in the data-flow, which is out of control of
        // the aforementioned service engineer.
        concerns = [{ message: httpErrorMessage }]
        Sentry.captureMessage('unsolvable transfer-status', (scope) => {
          scope.setExtra('response', response)
          return scope
        })
        break
    }

    return {
      status: Status.FAILURE,
      reason,
      concerns,
    }
  }
}
